import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { DollarSign, CheckCircle, FileText, Upload, FileSignature, PenLine, Sparkles, Circle } from 'lucide-react'
import { CertifIDVerification, type CertifIDResult } from './CertifIDVerification'
import { documentExtraction } from '../../services/documentExtraction'
import { SaleWizard } from './SaleWizard'
import { HubShell, StepTabs, ConfirmationToggle, type HubStep } from './HubShell'
import { DocumentChecklist, type DocumentStatus } from './DocumentChecklist'
import { DocumentSigningModal } from './DocumentSigningModal'
import { DocumentReadinessPanel } from '../../services/esign/DocumentReadinessPanel'
import { evaluateMultiple } from '../../services/esign/useDocumentReadiness'
import { DISBURSEMENT_HUB_DOCUMENTS } from '../../config/hubDocuments'
import { Button } from '../ui/Button'
import toast from 'react-hot-toast'
import { useHubData } from '../../hooks/useHubData'
import { useHubAccess } from '../../hooks/useHubAccess'
import { useDemoAcquisitionPrefill } from '../../hooks/useDemoAcquisitionPrefill'
import { useAuth } from '../../contexts/AuthContext'
import { useExchange } from '../../contexts/ExchangeContext'
import { useExchangeHubStatus } from '../../hooks/useExchangeHubStatus'
import { activityLog } from '../../services/activityLog'
import type { DocumentRenderData } from '../../services/esign/types'

type DisbursementStep = 'wire-instructions' | 'documents'
type WireUploadState = 'idle' | 'verifying' | 'verified'
type NeelaWirePhase = 'idle' | 'extracting' | 'revealed'

const NEELA_WIRE_STEPS = [
  'Reading wire instructions…',
  'Locating recipient and bank…',
  'Extracting routing and account…',
  'Cross-referencing Disbursement Hub…',
]

const HUB_ID = 'disbursement'

const TAB_CONFIRM_LABELS: Record<string, string> = {
  'wire-instructions': 'I confirm the disbursement form fields are complete and accurate.',
  'documents': 'I confirm all required disbursement documents are uploaded and reviewed.',
}

function confirmationStorageKey(userId: string): string {
  return `${HUB_ID}_step_wire-authorization_confirmed_${userId}`
}

function tabConfirmStorageKey(userId: string, tab: string): string {
  return `${HUB_ID}_tab_${tab}_confirmed_${userId}`
}

function loadSalePriceFromWizard(exchangeId: string): number {
  for (const scope of ['phase1', 'initial'] as const) {
    try {
      const raw = localStorage.getItem(`sale_wizard_v1_${exchangeId}_${scope}`)
      if (!raw) continue
      const parsed = JSON.parse(raw) as { listPrice?: string | number }
      const numeric = typeof parsed.listPrice === 'number'
        ? parsed.listPrice
        : parseFloat(String(parsed.listPrice ?? '').replace(/[^0-9.]/g, ''))
      if (Number.isFinite(numeric) && numeric > 0) return numeric
    } catch { }
  }
  try {
    const raw = localStorage.getItem('calculator_results')
    if (raw) {
      const parsed = JSON.parse(raw) as { input?: { sellingPrice?: number } }
      const price = typeof parsed?.input?.sellingPrice === 'number' ? parsed.input.sellingPrice : 0
      if (price > 0) return price
    }
  } catch { }
  return 0
}

function loadEscrowBalance(userId: string): number {
  try {
    const raw = localStorage.getItem(`escrow_data_${userId}`)
    if (raw) {
      const parsed = JSON.parse(raw) as { principalAmount?: number }
      if (typeof parsed.principalAmount === 'number' && parsed.principalAmount > 0) return parsed.principalAmount
    }
  } catch { }
  return loadSalePriceFromWizard(userId)
}

export const DisbursementHub: React.FC<{ onClose: () => void; onSectionComplete?: () => void }> = ({ onClose, onSectionComplete }) => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const { updateStatus } = useExchangeHubStatus()
  const [activeStep, setActiveStep] = useState<DisbursementStep>('wire-instructions')
  const [isFormComplete, setIsFormComplete] = useState(false)
  const [isConfirmed, setIsConfirmedState] = useState(false)
  const [tabConfirmations, setTabConfirmations] = useState<Record<string, boolean>>({
    'wire-instructions': false,
    'documents': false,
  })
  const [isSubmitted, setIsSubmitted] = useState(() => localStorage.getItem('disbursement_hub_submitted') === 'true')
  const controlsRef = useRef<any | null>(null)
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const { getData, saveData, flush } = useHubData('acquisition')
  const access = useHubAccess('disbursement', user)
  useDemoAcquisitionPrefill(getData, saveData)

  const [escrowBalance, setEscrowBalance] = useState<number>(() => (user?.id ? loadEscrowBalance(user.id) : 0))

  useEffect(() => {
    if (!user?.id) return
    const refresh = () => setEscrowBalance(loadEscrowBalance(user.id!))
    window.addEventListener('escrowDataUpdated', refresh)
    window.addEventListener('escrowHubCompleted', refresh)
    return () => {
      window.removeEventListener('escrowDataUpdated', refresh)
      window.removeEventListener('escrowHubCompleted', refresh)
    }
  }, [user?.id])

  const formattedBalance = escrowBalance > 0
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(escrowBalance)
    : 'Pending'

  const [disbursementAuthSigned, setDisbursementAuthSigned] = useState(() =>
    localStorage.getItem('disbursement_auth_signed') === 'true'
  )
  const [showSigningModal, setShowSigningModal] = useState(false)

  const [wireUploadState, setWireUploadState] = useState<WireUploadState>(() =>
    localStorage.getItem('wire_confirmation_verified') === 'true' ? 'verified' : 'idle'
  )
  const [wireUploadFile, setWireUploadFile] = useState<File | null>(null)
  const wireVerifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [certifIDResult, setCertifIDResult] = useState<CertifIDResult | null>(null)
  const [neelaWirePhase, setNeelaWirePhase] = useState<NeelaWirePhase>('idle')
  const [neelaWireFile, setNeelaWireFile] = useState<File | null>(null)
  const [neelaWireStepIndex, setNeelaWireStepIndex] = useState(0)
  const neelaWireStepTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const neelaWireInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => { document.body.classList.remove('modal-open') }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    const stored = localStorage.getItem(confirmationStorageKey(user.id))
    if (stored === 'true') setIsConfirmedState(true)
    const next: Record<string, boolean> = {}
    let anyTrue = false
    for (const tab of Object.keys(TAB_CONFIRM_LABELS)) {
      const val = localStorage.getItem(tabConfirmStorageKey(user.id, tab)) === 'true'
      next[tab] = val
      if (val) anyTrue = true
    }
    if (anyTrue) setTabConfirmations(next)
  }, [user?.id])

  useEffect(() => {
    const update = () => {
      if (!controlsRef.current?.isStepComplete) return
      setIsFormComplete(controlsRef.current.isStepComplete(11))
    }
    const t = setTimeout(update, 100)
    const i = setInterval(update, 1000)
    return () => { clearTimeout(t); clearInterval(i) }
  }, [activeStep])

  useEffect(() => {
    const handleAutoSaveUpdate = () => {
      const savedTime = controlsRef.current?.getLastSavedAt?.()
      if (savedTime) setLastSavedAt(savedTime)
    }
    window.addEventListener('wizardDataUpdated', handleAutoSaveUpdate)
    handleAutoSaveUpdate()
    return () => window.removeEventListener('wizardDataUpdated', handleAutoSaveUpdate)
  }, [])

  useEffect(() => {
    const handler = () => {
      setIsSubmitted(true)
      activityLog.logDisbursementSubmitted()
    }
    window.addEventListener('disbursementHubCompleted', handler)
    return () => window.removeEventListener('disbursementHubCompleted', handler)
  }, [])

  useEffect(() => {
    const check = () => {
      const submitted = localStorage.getItem('disbursement_hub_submitted') === 'true'
      if (submitted !== isSubmitted) setIsSubmitted(submitted)
    }
    check()
    const i = setInterval(check, 2000)
    return () => clearInterval(i)
  }, [isSubmitted])

  useEffect(() => {
    return () => {
      if (wireVerifyTimerRef.current) clearTimeout(wireVerifyTimerRef.current)
      if (neelaWireStepTimerRef.current) clearInterval(neelaWireStepTimerRef.current)
    }
  }, [])

  useEffect(() => {
    return () => {
      flush?.()
    }
  }, [flush])

  const handleConfirmChange = useCallback((checked: boolean) => {
    setIsConfirmedState(checked)
    if (!user?.id) return
    if (checked) {
      localStorage.setItem(confirmationStorageKey(user.id), 'true')
    } else {
      localStorage.removeItem(confirmationStorageKey(user.id))
    }
  }, [user?.id])

  const handleTabConfirmChange = useCallback((tab: string, checked: boolean) => {
    setTabConfirmations((prev) => ({ ...prev, [tab]: checked }))
    if (!user?.id) return
    if (checked) {
      localStorage.setItem(tabConfirmStorageKey(user.id, tab), 'true')
    } else {
      localStorage.removeItem(tabConfirmStorageKey(user.id, tab))
    }
  }, [user?.id])

  const handleSignComplete = (signature: string) => {
    if (!isFormComplete || !tabConfirmations['wire-instructions']) return
    const signedDate = new Date().toISOString()
    const signedBy = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'
    localStorage.setItem('disbursement_auth_signed', 'true')
    localStorage.setItem('disbursement_auth_signature', signature)
    localStorage.setItem('disbursement_auth_signed_date', signedDate)
    localStorage.setItem('disbursement_auth_signed_by', signedBy)
    updateStatus({
      documentSignatures: {
        disbursement_authorization: { signed: true, date: signedDate, by: signedBy },
      },
    }).catch(() => undefined)
    setDisbursementAuthSigned(true)
    setShowSigningModal(false)
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    toast.success('Disbursement Authorization signed.')
  }

  const handleWireUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    if (!file) return
    setWireUploadFile(file)
    setWireUploadState('verifying')
    wireVerifyTimerRef.current = setTimeout(() => {
      const verifiedDate = new Date().toISOString()
      localStorage.setItem('wire_confirmation_verified', 'true')
      localStorage.setItem('wire_confirmation_verified_date', verifiedDate)
      updateStatus({
        documentSignatures: {
          wire_confirmation: { signed: true, date: verifiedDate, by: 'upload' },
        },
      }).catch(() => undefined)
      setWireUploadState('verified')
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    }, 1500)
  }

  const handleNeelaWireUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    if (!file) return
    setNeelaWireFile(file)
    setNeelaWireStepIndex(0)
    setNeelaWirePhase('extracting')
    neelaWireStepTimerRef.current = setInterval(() => {
      setNeelaWireStepIndex((prev) => Math.min(prev + 1, NEELA_WIRE_STEPS.length - 1))
    }, 500)
    documentExtraction.simulateExtraction(file, 'wire-instructions').then((extracted) => {
      if (neelaWireStepTimerRef.current) {
        clearInterval(neelaWireStepTimerRef.current)
        neelaWireStepTimerRef.current = null
      }
      setNeelaWireStepIndex(NEELA_WIRE_STEPS.length - 1)
      if (controlsRef.current?.setDisbursementForm) {
        controlsRef.current.setDisbursementForm((prev: Record<string, string>) => ({
          ...prev,
          ...(extracted.exchangerNames ? { exchangerNames: extracted.exchangerNames } : {}),
          ...(extracted.wireRecipient ? { wireRecipient: extracted.wireRecipient } : {}),
          ...(extracted.wireRecipientAddress ? { wireRecipientAddress: extracted.wireRecipientAddress } : {}),
          ...(extracted.wireRecipientBankName ? { wireRecipientBankName: extracted.wireRecipientBankName } : {}),
          ...(extracted.wireRecipientBankABA ? { wireRecipientBankABA: extracted.wireRecipientBankABA } : {}),
          ...(extracted.wireRecipientBankAccount ? { wireRecipientBankAccount: extracted.wireRecipientBankAccount } : {}),
          ...(extracted.beneficiaryName ? { beneficiaryName: extracted.beneficiaryName } : {}),
          ...(extracted.referenceNumber ? { referenceNumber: extracted.referenceNumber } : {}),
        }))
      }
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
      setNeelaWirePhase('revealed')
    })
  }, [])

  const handleNeelaWireReset = useCallback(() => {
    setNeelaWirePhase('idle')
    setNeelaWireFile(null)
    setNeelaWireStepIndex(0)
    if (neelaWireInputRef.current) neelaWireInputRef.current.value = ''
  }, [])

  const handleSubmit = () => {
    try {
      if (controlsRef.current?.submit) {
        controlsRef.current.submit()
      } else {
        toast.error('Wizard not ready. Please wait a moment and try again.')
      }
    } catch {
      toast.error('Failed to submit. Please try again.')
    }
  }

  const disbursementFormData = controlsRef.current?.getFormData?.()?.disbursementForm

  const wireFieldsSet = !!(
    disbursementFormData?.wireRecipient &&
    disbursementFormData?.wireRecipientBankName &&
    disbursementFormData?.wireRecipientBankABA &&
    disbursementFormData?.wireRecipientBankAccount &&
    disbursementFormData?.beneficiaryName
  )

  const disbursementDocRenderData: DocumentRenderData = useMemo(() => {
    const open1031FileNumber = currentExchange?.id
      ? `OPEN1031-${currentExchange.id.replace(/-/g, '').slice(0, 8).toUpperCase()}`
      : undefined
    return {
      exchanger: {
        names: disbursementFormData?.exchangerNames || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : ''),
        homeAddress: '',
        taxIdNumber: '',
      },
      disbursement: {
        amount: disbursementFormData?.amount || '',
        beneficiaryName: disbursementFormData?.beneficiaryName || '',
        recipientBankName: disbursementFormData?.wireRecipientBankName || '',
        escrowDate: localStorage.getItem('escrow_hub_submitted_date') || new Date().toISOString(),
        bankName: 'First Foundation Bank',
        exchangeAccountNumber: '5805101726',
        disbursementMethod: 'wire',
        wireInstructionsAttached: Boolean(
          disbursementFormData?.wireRecipientBankABA && disbursementFormData?.wireRecipientBankAccount,
        ),
      },
      bundleMeta: {
        open1031FileNumber,
        intermediaryOfficerName: 'Tom Bottenberg',
        intermediaryOfficerTitle: 'Chief Operations Officer',
      },
    }
  }, [disbursementFormData, user, currentExchange])

  const { allReady: docsReady, perDocument: docReadiness } = useMemo(
    () => evaluateMultiple(['disbursement-authorization'], disbursementDocRenderData),
    [disbursementDocRenderData]
  )

  const steps: HubStep[] = [
    { key: 'wire-instructions', label: 'Disbursement Form', icon: DollarSign, completed: isFormComplete },
    {
      key: 'documents',
      label: 'Documents',
      icon: FileText,
      completed: isSubmitted,
      disabled: !docsReady,
      disabledReason: 'Complete the disbursement form above before signing',
    },
  ]

  const canSignAuth = isFormComplete && tabConfirmations['wire-instructions']

  const canSubmit = isFormComplete && isConfirmed && !isSubmitted

  if (!access.canAccess) {
    return (
      <div className="modal-overlay">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6 my-4">
          <div className="text-center">
            <DollarSign className="h-12 w-12 text-orange-600 dark:text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Disbursement Hub Locked</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {access.message || 'Please complete the Purchase Hub first'}
            </p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    )
  }

  const initialData = {
    disbursementForm: getData('disbursementForm'),
  }

  const documentStatuses: Record<string, DocumentStatus> = {}
  DISBURSEMENT_HUB_DOCUMENTS.forEach(doc => {
    if (doc.name === 'Disbursement Authorization') {
      documentStatuses[doc.name] = { uploaded: disbursementAuthSigned, signed: disbursementAuthSigned }
    } else if (doc.name === 'Wire Transfer Confirmation') {
      documentStatuses[doc.name] = { uploaded: wireUploadState === 'verified', signed: false }
    } else {
      documentStatuses[doc.name] = { uploaded: isSubmitted, signed: false }
    }
  })

  return (
    <>
      <HubShell
        icon={DollarSign}
        title="Disbursement Hub"
        subtitle="Wire details → authorize → release."
        completionPercent={access.completionPercent}
        isSubmitted={isSubmitted}
        color="orange"
        onClose={onClose}
        canSubmit={canSubmit}
        completedMessage="Disbursement Hub Completed"
        nextHubMessage="Exchange complete — review your documents"
        onSubmit={handleSubmit}
        submitLabel={isSubmitted ? 'Submitted' : !isConfirmed ? 'Confirm to Submit' : 'Submit Hub'}
        lastSavedAt={lastSavedAt}
      >
        {escrowBalance > 0 && (
          <div className="rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4 flex items-center justify-between">
            <span className="text-sm font-medium text-orange-800 dark:text-orange-200">Available Escrow Balance</span>
            <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">{formattedBalance}</span>
          </div>
        )}

        <StepTabs
          steps={steps}
          activeStep={activeStep}
          onStepChange={(key) => {
            flush?.()
            setActiveStep(key as DisbursementStep)
          }}
          sectionTitle="Wire Transfer Disbursement"
          color="orange"
          isSubmitted={isSubmitted}
        />

        {activeStep === 'wire-instructions' && (
          <div className="flex-1 overflow-hidden">
            {!isSubmitted && neelaWirePhase === 'idle' && (
              <div className="mb-4 rounded-xl border border-orange-200 dark:border-orange-700 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-400" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                      Neela can pre-fill your wire instructions
                    </p>
                    <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5 mb-3">
                      Upload your wire instructions document and Neela will extract the fields below automatically.
                    </p>
                    <input
                      ref={neelaWireInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleNeelaWireUpload}
                      className="sr-only"
                      aria-label="Upload wire instructions for Neela extraction"
                      id="neela-wire-upload"
                    />
                    <label
                      htmlFor="neela-wire-upload"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-orange-300 dark:border-orange-600 bg-white dark:bg-slate-800 text-sm font-medium text-orange-700 dark:text-orange-300 cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-900/20 focus-within:ring-2 focus-within:ring-orange-400 transition-colors"
                    >
                      <Upload className="h-4 w-4" aria-hidden="true" />
                      Upload wire instructions
                    </label>
                  </div>
                </div>
              </div>
            )}

            {neelaWirePhase === 'extracting' && (
              <div
                className="mb-4 rounded-xl border border-orange-200 dark:border-orange-700 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 p-5"
                role="status"
                aria-live="polite"
                aria-label="Neela is reading your wire instructions"
                data-testid="neela-wire-extracting"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-400 animate-pulse" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-0.5">
                      Neela is reading your wire instructions…
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 truncate mb-3">{neelaWireFile?.name ?? ''}</p>
                    <div className="space-y-1.5">
                      {NEELA_WIRE_STEPS.map((step, index) => (
                        <div key={step} className="flex items-center gap-2">
                          {index < neelaWireStepIndex ? (
                            <CheckCircle className="h-3 w-3 text-orange-500 flex-shrink-0" aria-hidden="true" />
                          ) : index === neelaWireStepIndex ? (
                            <span className="h-3 w-3 rounded-full border-2 border-orange-500 border-t-transparent animate-spin flex-shrink-0" aria-hidden="true" />
                          ) : (
                            <Circle className="h-3 w-3 text-orange-300 dark:text-orange-700 flex-shrink-0" aria-hidden="true" />
                          )}
                          <span className={index <= neelaWireStepIndex
                            ? 'text-xs text-orange-800 dark:text-orange-200'
                            : 'text-xs text-orange-400 dark:text-orange-600'
                          }>
                            {step}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {neelaWirePhase === 'revealed' && (
              <div
                className="mb-4 rounded-xl border-2 border-orange-300 dark:border-orange-600 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/20 p-4"
                role="region"
                aria-label="Wire instructions extraction confirmed"
                data-testid="neela-wire-reveal"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-400" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                      Neela extracted your wire instructions
                    </p>
                    <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5 mb-1">
                      Recipient, bank, and routing fields are now pre-filled. Review and confirm.
                    </p>
                    <button
                      type="button"
                      onClick={handleNeelaWireReset}
                      className="text-xs text-orange-600 dark:text-orange-400 underline hover:text-orange-700 dark:hover:text-orange-300"
                    >
                      Replace document
                    </button>
                  </div>
                </div>
              </div>
            )}

            <SaleWizard
              embedded
              hideFooter
              exposeControls={(api) => (controlsRef.current = api)}
              mode="disbursement"
              onClose={onClose}
              onComplete={() => { setIsFormComplete(true); onSectionComplete?.() }}
              initialData={initialData}
            />
            <ConfirmationToggle
              id="confirm-disbursement-wire"
              label={TAB_CONFIRM_LABELS['wire-instructions']}
              checked={isSubmitted ? true : (tabConfirmations['wire-instructions'] ?? false)}
              onChange={(checked) => handleTabConfirmChange('wire-instructions', checked)}
              disabled={isSubmitted}
              visible={wireFieldsSet || isFormComplete || isSubmitted}
            />
          </div>
        )}

        {activeStep === 'documents' && (
          <div className="space-y-4">
            <DocumentReadinessPanel readiness={docReadiness} />
            <DocumentChecklist
              documents={DISBURSEMENT_HUB_DOCUMENTS}
              statuses={documentStatuses}
              hubColor="orange"
              onView={(docName) => {
                if (docName === 'Disbursement Request') setShowSigningModal(true)
              }}
              readOnly={isSubmitted}
            />

            {!isSubmitted && (
              <CertifIDVerification
                context="disbursement-outbound"
                onVerified={(result) => setCertifIDResult(result)}
              />
            )}
            {certifIDResult && (
              <p className="text-xs text-orange-700 dark:text-orange-300 sr-only" aria-live="polite">
                Payee verified by CertifID
              </p>
            )}

            {!isSubmitted && !disbursementAuthSigned && (
              <div className="p-4 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <FileSignature className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Disbursement Authorization</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Sign to authorize the QI to release exchange funds to the replacement-property closing company.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowSigningModal(true)}
                    disabled={!canSignAuth}
                    className="ml-4 shrink-0"
                  >
                    <PenLine className="h-4 w-4 mr-1.5" />
                    Sign Now
                  </Button>
                </div>
                {!canSignAuth && (
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-2">
                    Complete and confirm the wire instructions before signing
                  </p>
                )}
              </div>
            )}

            {disbursementAuthSigned && (
              <div className="p-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-sm font-medium text-green-700 dark:text-green-300">Disbursement Authorization signed</p>
              </div>
            )}

            {isFormComplete && wireUploadState === 'idle' && (
              <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-3">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Upload Bank Wire Confirmation
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Upload the bank-issued receipt confirming the wire was transmitted. Accepts PDF or image.
                </p>
                <div>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleWireUpload}
                    className="sr-only focus-visible:ring-orange-400"
                    aria-label="Upload wire confirmation document"
                    id="wire-confirmation-upload"
                  />
                  <label
                    htmlFor="wire-confirmation-upload"
                    className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-orange-300 dark:border-orange-600 rounded-lg cursor-pointer hover:border-orange-400 dark:hover:border-orange-500 focus-within:ring-2 focus-within:ring-orange-400 transition-colors"
                  >
                    <Upload className="h-8 w-8 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                      Click to upload wire confirmation
                    </span>
                    <span className="text-xs text-slate-400">PDF, PNG, or JPG</span>
                  </label>
                </div>
              </div>
            )}

            {wireUploadState === 'verifying' && (
              <div className="flex flex-col items-center gap-3 py-4" role="status" aria-label="Verifying wire confirmation">
                <div className="relative h-10 w-10">
                  <div className="absolute inset-0 rounded-full border-2 border-orange-200 dark:border-orange-800"></div>
                  <div className="absolute inset-0 rounded-full border-t-2 border-orange-500 animate-spin"></div>
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Verifying…</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Extracting wire details from your document</p>
              </div>
            )}

            {wireUploadState === 'verified' && wireUploadFile && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-green-700 dark:text-green-300">Wire Confirmation Verified</p>
                  <p className="text-xs text-green-600 dark:text-green-400 truncate">{wireUploadFile.name}</p>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold flex-shrink-0">
                  <FileText className="h-3 w-3" />
                  Verified
                </span>
              </div>
            )}

            {wireUploadState === 'verified' && !wireUploadFile && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <p className="text-xs font-semibold text-green-700 dark:text-green-300">Wire Confirmation previously verified</p>
              </div>
            )}

            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Auto-Generated Documents</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Exchange closing statements, the transaction report, and IRS Form 8824 are automatically
                    generated from your exchange data. Visit the Forms page to review the 8824 auto-fill preview.
                  </p>
                </div>
              </div>
            </div>
            <ConfirmationToggle
              id="confirm-disbursement-documents"
              label={TAB_CONFIRM_LABELS['documents']}
              checked={isSubmitted ? true : (tabConfirmations['documents'] ?? false)}
              onChange={(checked) => handleTabConfirmChange('documents', checked)}
              disabled={isSubmitted}
              visible={disbursementAuthSigned || isSubmitted}
            />
          </div>
        )}

        <ConfirmationToggle
          id="confirm-disbursement"
          label="I confirm the wire instructions are accurate."
          checked={isSubmitted ? true : isConfirmed}
          onChange={handleConfirmChange}
          disabled={isSubmitted}
          visible={wireFieldsSet || isFormComplete || isSubmitted}
        />
      </HubShell>

      {showSigningModal && (
        <DocumentSigningModal
          documentName="Disbursement Request"
          documentType="disbursement-authorization"
          exchangerData={disbursementDocRenderData.exchanger}
          disbursementData={disbursementDocRenderData.disbursement}
          bundleMeta={disbursementDocRenderData.bundleMeta}
          signerEmail={user?.email || ''}
          exchangeId={currentExchange?.id || user?.id || ''}
          onSign={handleSignComplete}
          onClose={() => setShowSigningModal(false)}
          viewOnly={disbursementAuthSigned || isSubmitted}
        />
      )}
    </>
  )
}
